import { observer } from 'mobx-react-lite'

import { CrmCommentsAside } from '@/Containers/CrmSection/Components/Comments'
import { Form, FormField } from '@/Core/UI/Form'
import { EntityBody, EntityHero, EntityLayout, FieldCell, FieldGrid, FieldSection } from '@/Ship/Components/EntityCard'

import { OtherCarrierCardHeader } from '../Components/OtherCarrierCardHeader'
import { OTHER_CARRIER_COMMENT_REST_URL } from '../Contracts/IOtherCarrier'

import type { OtherCarrierCrudContextLike, OtherCarrierFormContextLike } from '../Contracts/IOtherCarrier'
import type { ReactNode } from 'react'

interface Props {
    crudContext: OtherCarrierCrudContextLike | undefined
    formContext: OtherCarrierFormContextLike | undefined
}

/**
 * Блоки картки (`SPEC-010` §9.7):
 *
 *  - **Головне** — порядок легасі (`OtherCarrierFormTemplate.jsx:10-16`):
 *    назва, ЄДРПОУ, типи авто, країни, далі довгі тексти на всю ширину;
 *  - **Службова інформація** — хто й коли створив і змінив, лише в
 *    збереженого запису.
 */
const SECTIONS = {
    main: ['title', 'edrpou', 'carTypes', 'countries', 'contactInformation', 'description', 'comment'],
    service: ['createdAt', 'createdBy', 'updatedAt', 'updatedBy'],
} as const

/** На всю ширину: довгі тексти. */
const WIDE = new Set<string>(['contactInformation', 'description', 'comment'])

/** «Ід» — колонка й фільтр реєстру, а не поле картки. */
const PLACED = new Set<string>([...Object.values(SECTIONS).flat(), 'id'])

/**
 * Картка іншого перевізника на спільних правилах карток (`EntityLayout`,
 * `SPEC-008` §7.1): вкладок немає, шапка — назва, ліворуч поля двома
 * стовпцями, праворуч — стрічка коментарів (у легасі — `TimeLine` у шухляді
 * 750, `OtherCarrierPage.jsx:6-8`). У формі створення на місці стрічки —
 * пояснення, коли вона з'явиться.
 *
 * Історії змін, обраного й експорту в іншого перевізника немає (ADR 041).
 *
 * ⚠️ Значень полів, які редагують, шаблон не читає — лише `uuid`
 * (`SPEC-002` §4.2): шапка й стрічка — окремі observer-компоненти.
 */
export const OtherCarrierFormTemplate = observer(({ crudContext, formContext }: Props) => {
    const fields = crudContext?.resource?.fields ?? {}
    const uuid = formContext?.values?.uuid
    const isNew = typeof uuid !== 'string' || uuid === ''

    // Поля, додані в ресурс пізніше, не мають зникати мовчки
    const rest = Object.keys(fields).filter((name) => !PLACED.has(name))

    const cell = (name: string): ReactNode =>
        name in fields ? (
            <FieldCell key={name} wide={WIDE.has(name)}>
                <FormField name={name} />
            </FieldCell>
        ) : null

    const grid = (names: readonly string[]) => (
        <Form formContext={formContext}>
            <FieldGrid>{names.map(cell)}</FieldGrid>
        </Form>
    )

    const main = (
        <>
            <FieldSection title="Головне">{grid(SECTIONS.main)}</FieldSection>
            <FieldSection title="Додатково" hidden={rest.length === 0}>
                {grid(rest)}
            </FieldSection>
            <FieldSection title="Службова інформація" hidden={isNew}>
                {grid(SECTIONS.service)}
            </FieldSection>
        </>
    )

    return (
        <EntityLayout>
            {!isNew && formContext ? (
                <EntityHero>
                    <OtherCarrierCardHeader formContext={formContext} />
                </EntityHero>
            ) : null}
            <EntityBody
                main={main}
                aside={
                    <CrmCommentsAside
                        restUrl={OTHER_CARRIER_COMMENT_REST_URL}
                        ownerField="otherCarrier"
                        formContext={formContext}
                        unsavedText="Коментувати можна буде після збереження перевізника."
                    />
                }
            />
        </EntityLayout>
    )
})
